import React, { FC } from 'react'
import { Box, Button, Typography, useTheme } from '@mui/material'

import { useTranslation } from 'react-i18next'
import { Profile } from '@/shared/store/useAppStore'
import LoginInputField from '@/pages/loginScreen/LoginInputField'
import { DataCheckbox } from '@/components/settings/components/DataCheckbox'
import { INewProfile } from '@/widgets/ProfileScreen/hooks/useProfilesScreen'
import useKeyDown from '@/shared/hooks/useKeyDown'

interface ProfileFormProps {
    profile: INewProfile | Profile | null
    setProfile: (value: string) => void
    setCheckBox: () => void
    isCreateProfileDisabled: boolean
    handleProfileAction: () => void
    cancelAction: () => void
    actionButtonText: string
    isCheckboxChecked?: boolean
}

export const ProfileForm: FC<ProfileFormProps> = ({
    profile,
    setProfile,
    setCheckBox,
    isCreateProfileDisabled,
    handleProfileAction,
    cancelAction,
    actionButtonText,
    isCheckboxChecked,
}) => {
    const { t: translate } = useTranslation()
    const theme = useTheme()
    const editContainer = {
        position: 'relative',
        padding: '0 8px',
    }
    const cancelButtonStyle = {
        marginTop: '12px',
        width: '100%',
        height: '36px',
        padding: '8px 12px',
        gap: '8px',
        borderRadius: '10px',
        bgcolor: 'transparent',
        border: '1px solid white',
        color: 'white',
    }
    const handleProfileStyle = {
        marginTop: '12px',
        width: '100%',
        height: '36px',
        padding: '8px 12px',
        gap: '8px',
        borderRadius: '10px',
        bgcolor: 'primary.main',
    }
    const addProfileStyle = {
        ...handleProfileStyle,
        bgcolor: isCreateProfileDisabled ? 'custom.lightGray' : 'primary.main',
    }
    const inputContainerStyle = {
        width: '100%',
        borderRadius: '10px',
        border: `1px solid ${theme.palette.custom.neutralLight}`,
        margin: '10px 0',
        paddingLeft: 0,
    }

    useKeyDown(handleProfileAction)

    return (
        <Box sx={editContainer}>
            <Typography variant="label3" mb="8px" color="white">
                {translate('userName')}
            </Typography>
            <LoginInputField
                placeholder={translate('setNewProfileName')}
                inputValue={profile?.profile_name || ''}
                setInputValue={(value: string) => setProfile(value)}
                styles={inputContainerStyle}
                native={{ maxLength: 24, autoComplete: 'off' }}
            />
            <Box mb="10px">
                <DataCheckbox
                    fieldName="underEighteen"
                    value={isCheckboxChecked || false}
                    onChange={() => setCheckBox()}
                    labelStyle={{
                        typography: 'label4',
                        color: 'white',
                        margin: 0,
                    }}
                />
            </Box>
            <Box display="flex" columnGap="16px">
                <Button
                    disabled={isCreateProfileDisabled}
                    sx={addProfileStyle}
                    onClick={handleProfileAction}>
                    <Typography color="black" variant="label4">
                        {translate(actionButtonText)}
                    </Typography>
                </Button>
                <Button
                    variant="outlined"
                    sx={cancelButtonStyle}
                    onClick={cancelAction}>
                    <Typography color="white" variant="label4">
                        {translate('globalCancel')}
                    </Typography>
                </Button>
            </Box>
        </Box>
    )
}
